/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, DEFAULT_SLOT, Link as LinkCore, LinkContext as LinkCoreContext, type LinkProps as LinkCoreProps, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import type { NumberBadgeProps } from '../number-badge/number-badge.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import type { TextProps } from '../text/text.component';
import { Text, TextContext } from '../text/text.component';

interface LinkButtonProps extends Omit<LinkCoreProps, 'style' | 'className'> {
  /** The variant of the button. (Default: primary) */
  variant?: keyof typeof variantVariants;
  /** The size of the button. (Default: 32) */
  size?: keyof typeof sizeVariants;
  /** Whether the button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const LinkButtonContext = createContext<ContextValue<LinkButtonProps, HTMLAnchorElement>>({});

const LinkButton = forwardRef((props: LinkButtonProps, ref: ForwardedRef<HTMLAnchorElement>) => {
  [props, ref] = useContextProps(props, ref, LinkButtonContext);

  const { style, isRounded = false, variant = 'primary', children, size = 32, ...coreProps } = props;

  return (
    <LinkCore
      ref={ref}
      {...stylex.props(
        styles.base,
        variantVariants[variant],
        sizeVariants[size],
        isRounded && styles.rounded,
        coreProps.isDisabled && styles.disabled,
        style,
      )}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'quaternary' ||
          variant === 'primary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primaryHover' ||
            'white'
          ) ||
          variant === 'primaryMono' && 'inverted' ||
          variant === 'secondary' && 'black' ||
          variant === 'tertiary' && 'tertiaryHover' ||
          variant === 'quaternary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'secondary'
          ) ||
          variant === 'alternative' && 'black' ||
          variant === 'ghost' && (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'tertiary'
          ) ||
          'interactive'
        );

        const numberBadgeColor = (
          (renderProps.isDisabled && 'disabled') ||
          (['primary', 'primaryMono', 'tertiary'].includes(variant) && 'informational') ||
          'neutral'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                style: textStyles.base,
                size: TEXT_SIZES[size],
                color: renderProps.isDisabled ? 'buttonContentDisabled' : contentColor
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: iconStyles.base,
                    color: contentColor,
                  },
                  [LINK_BUTTON_SLOTS.LEFT]: {
                    size: LEFT_ICON_SIZE[size],
                    color: contentColor,
                    style: [iconStyles.base, accessoryLeftStyles[size]],
                  },
                  [LINK_BUTTON_SLOTS.RIGHT]: {
                    size: RIGHT_ICON_SIZE[size],
                    color: contentColor,
                    style: [iconStyles.base, accessoryRightStyles[size]],
                  }
                }
              }],
              [NumberBadgeContext, {
                slots: {
                  [LINK_BUTTON_SLOTS.LEFT]: {
                    style: accessoryLeftStyles[size],
                    size: NUMBER_BADGE_SIZES[size],
                    isPastel: false,
                    color: numberBadgeColor,
                  },
                  [LINK_BUTTON_SLOTS.RIGHT]: {
                    style: accessoryRightStyles[size],
                    size: NUMBER_BADGE_SIZES[size],
                    isPastel: false,
                    color: numberBadgeColor,
                  }
                }
              }]
            ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </LinkCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  24: 12,
  28: 12,
  32: 13,
  40: 14,
  48: 16,
  52: 16,
  64: 20,
};

const LEFT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const RIGHT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 16,
  64: 16,
};

const NUMBER_BADGE_SIZES: Record<keyof typeof sizeVariants, NumberBadgeProps['size']> = {
  24: 16,
  28: 16,
  32: 16,
  40: 20,
  48: 20,
  52: 24,
  64: 24,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 0,
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    position: 'relative',
    textDecoration: 'none',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color, border',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
  rounded: { borderRadius: 999 },
  disabled: {
    backgroundColor: $.buttonSurfaceDisabled,
    borderColor: 'transparent',
  },
});

const variantVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: $.buttonSurfacePrimary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfacePrimaryHover,
    },
  },
  primaryMono: {
    backgroundColor: {
      default: $.buttonSurfacePrimaryMono,
      ':is([data-hovered], [data-pressed])': $.buttonSurfacePrimaryMonoHover,
    },
  },
  secondary: {
    backgroundColor: {
      default: $.buttonSurfaceSecondary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceSecondaryHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceTertiaryHover,
    },
  },
  quaternary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceQuaternaryHover,
    },
    borderColor: {
      default: $.border10,
      ':is([data-hovered], [data-pressed])': 'transparent',
    },
    borderWidth: 1,
  },
  alternative: {
    backgroundColor: {
      default: $.buttonSurfaceAlternative,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceAlternativeHover,
    },
  },
  ghost: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed])': $.opacity04,
    }
  },
});

const sizeVariants = stylex.create({
  24: {
    borderRadius: 6,
    height: 24,
    paddingInline: 8,
  },
  28: {
    borderRadius: 6,
    height: 28,
    paddingInline: 12,
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingInline: 12,
  },
  40: {
    borderRadius: 8,
    height: 40,
    paddingInline: 16,
  },
  48: {
    borderRadius: 10,
    height: 48,
    paddingInline: 20,
  },
  52: {
    borderRadius: 10,
    height: 52,
    paddingInline: 28,
  },
  64: {
    borderRadius: 14,
    height: 64,
    paddingInline: 32,
  },
});

const textStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const accessoryLeftStyles = stylex.create({
  24: { marginRight: 6 },
  28: { marginRight: 6 },
  32: { marginRight: 8 },
  40: { marginRight: 6 },
  48: { marginRight: 8 },
  52: { marginRight: 10 },
  64: { marginRight: 12 },
});

const accessoryRightStyles = stylex.create({
  24: { marginLeft: 4 },
  28: { marginLeft: 4 },
  32: { marginLeft: 4 },
  40: { marginLeft: 6 },
  48: { marginLeft: 6 },
  52: { marginLeft: 8 },
  64: { marginLeft: 8 },
});

const LINK_BUTTON_SLOTS = {
  LEFT: 'left',
  RIGHT: 'right',
};

export { LINK_BUTTON_SLOTS, LinkButton, LinkButtonContext, LinkCore, LinkCoreContext, type LinkButtonProps };
